export const mentionCandidates = [{ id: 0, display: "PREP" }];

export const updateMentionCandidates = (profiles) => {
    const nextCandidates = Array.isArray(profiles)
        ? profiles.map((profile) => ({
              id: profile.id,
              display: profile.name,
          }))
        : [];

    mentionCandidates.splice(1, mentionCandidates.length - 1, ...nextCandidates);
};

export const mentionReplaceRegex = /@\[([^\]]+)\]\(([^)]+)\)/g;

export const createEmptyMessageDoc = () => ({
    type: "doc",
    content: [{ type: "paragraph" }],
});

export const isMessageDoc = (value) => {
    return !!value && typeof value === "object" && value.type === "doc" && Array.isArray(value.content);
};

export const parseMessageDoc = (value) => {
    if (!value) return createEmptyMessageDoc();
    if (isMessageDoc(value)) return value;
    if (typeof value !== "string") return createEmptyMessageDoc();

    const parsed = JSON.parse(value);
    if (!isMessageDoc(parsed)) {
        throw new Error("Invalid message document.");
    }
    return parsed;
};

export const stringifyMessageDoc = (doc) => JSON.stringify(isMessageDoc(doc) ? doc : createEmptyMessageDoc());

export const normalizeMessageDocString = (value) => stringifyMessageDoc(parseMessageDoc(value));

const textSegmentToNodes = (text) => (text ? [{ type: "text", text }] : []);

const legacyLineToNodes = (line) => {
    const nodes = [];
    let cursor = 0;
    let match;
    const regex = new RegExp(mentionReplaceRegex.source, "g");

    while ((match = regex.exec(line)) !== null) {
        nodes.push(...textSegmentToNodes(line.slice(cursor, match.index)));
        nodes.push({
            type: "mention",
            attrs: {
                id: match[2],
                label: match[1],
            },
        });
        cursor = match.index + match[0].length;
    }

    nodes.push(...textSegmentToNodes(line.slice(cursor)));
    return nodes;
};

export const legacyTextToMessageDoc = (text) => {
    const lines = String(text || "").split(/\r?\n/);
    return {
        type: "doc",
        content: lines.map((line) => {
            const content = legacyLineToNodes(line);
            return content.length ? { type: "paragraph", content } : { type: "paragraph" };
        }),
    };
};

const appendNodeText = (node, parts) => {
    if (!node) return;

    if (node.type === "text") {
        parts.push(node.text || "");
        return;
    }

    if (node.type === "mention") {
        parts.push(`@${node.attrs?.label || node.attrs?.id || ""}`);
        return;
    }

    if (node.type === "hardBreak") {
        parts.push("\n");
        return;
    }

    if (Array.isArray(node.content)) {
        node.content.forEach((child) => appendNodeText(child, parts));
    }
};

export const messageDocToPlainText = (value) => {
    const doc = parseMessageDoc(value);
    const paragraphs = (doc.content || []).map((node) => {
        const parts = [];
        appendNodeText(node, parts);
        return parts.join("");
    });
    return paragraphs.join("\n");
};

export const isMessageDocEmpty = (value) => messageDocToPlainText(value).trim() === "";

const visitMessageDocNodes = (node, visitor) => {
    if (!node) return;
    visitor(node);
    if (Array.isArray(node.content)) {
        node.content.forEach((child) => visitMessageDocNodes(child, visitor));
    }
};

export const messageDocHasMention = (value, mentionId) => {
    const id = String(mentionId);
    let found = false;
    visitMessageDocNodes(parseMessageDoc(value), (node) => {
        if (node.type === "mention" && String(node.attrs?.id) === id) {
            found = true;
        }
    });
    return found;
};

export const messageDocHasPrepMention = (value) => messageDocHasMention(value, "0");

export const collectMessageDocMentionIds = (value) => {
    const ids = [];
    visitMessageDocNodes(parseMessageDoc(value), (node) => {
        if (node.type === "mention" && node.attrs?.id != null) {
            ids.push(Number(node.attrs.id));
        }
    });
    return ids;
};

export const prependMentionToMessageDoc = (value, mention) => {
    const doc = parseMessageDoc(value);
    const paragraph = doc.content?.[0] || { type: "paragraph", content: [] };
    const content = Array.isArray(paragraph.content) ? [...paragraph.content] : [];
    return stringifyMessageDoc({
        ...doc,
        content: [
            {
                ...paragraph,
                content: [
                    {
                        type: "mention",
                        attrs: {
                            id: String(mention.id),
                            label: mention.display,
                        },
                    },
                    { type: "text", text: " " },
                    ...content,
                ],
            },
            ...(doc.content || []).slice(1),
        ],
    });
};
